import React, { useContext, useEffect, useRef, useState } from "react";
import classnames from "classnames";
import Skeleton from "react-loading-skeleton";
import Sticky from "react-stickynode";

import { AppContext, RestrictedEvents } from "@vahak/core/dist/app-context";
import { capitalize } from "@vahak/core/dist/methods/capitalize";
import { getRemValue } from "@vahak/core/dist/methods/getRemValue";
import searchStyles from "./search.module.scss";
import { PLAYSTORE_URLS } from "@vahak/core/dist/constants/link";
import { ROUTES } from "@vahak/core/dist/constants/routeStrings";

import AppPagination from "@vahak/core/dist/components/Pagination/pagination";
import BottomPagination from "@vahak/core/dist/components/Pagination/BottomPagination";

// import "./Search.scss";
import Mobile, { MobileProps } from "./mobile/Mobile";
import Desktop, { DesktopProps } from "./desktop/Desktop";
import { CitySearchResultWithCateg } from "@vahak/core/dist/custom-types/location-search";
import { Company } from "@vahak/core/dist/custom-types/company";
import { getCurrentState } from "@vahak/core/dist/methods/getCurrentState";
import { sleep } from "@vahak/core/dist/methods/sleep";
import { usePlaceSearch, useStateSearch } from "@vahak/core/dist/hooks/useSearch";
import DesktopSearchCard from "./DesktopSearchCard";
import SearchResultsContainer from "./SearchResultsContainer";

import CTABanner from "../../common/cta-banner/CTABanner";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { ga4sendEvent } from "@vahak/core/dist/_services/user-analytics.service";
import {
    transporterUtmParam,
    getPageLoadSourceUTMParam,
    marketplaceUtmParamTypes
} from "@vahak/core/dist/methods/getPageLoadSourceUTMParam";
// import EventScriptOnLoad from "utils/EventScriptOnLoad";
import { breadcrumbsConstants } from "../../../constants/breadcrumbs-constants";
import { useTransporterSearch } from "../../../services/useTransporterService";
import PageMetaTags from "../../common/page-meta-tags/PageMetaTags";
import ShowMoreText from "@vahak/core-ui/dist/components/ShowMoreText";
import Typography from "@vahak/core-ui/dist/components/Typography";
import CityDescription from "../helper/CityDescription";
import { getFaqListByCity } from "../helper/getFaqList";
import { useDebounce } from "@vahak/core/dist/hooks/useDebounce";
import useMediaQuery from "@vahak/core-ui/dist/hooks/useMediaQuery";
import { LocationSearchSource } from "@vahak/core/dist/_services/city-search.service";
import { GA4EventNames, SOURCE_UTM } from "@vahak/core/dist/constants";
import Button from "@vahak/core-ui/dist/components/Button";
import COLORS from "@vahak/core-ui/dist/constants/colors";
import AppImg from "../../utils/AppImg";
import MandateServiceType from "../../marketplace/mandate-service-type/MandateServiceType";
import { useEventTrackerService } from "@vahak/core/dist/_services";

const BOT_USER_AGENT_PATTERN = /bot|crawler|spider|googlebot|bingbot|msnbot|yandexbot|baiduspider|slurp|duckduckbot/i;

interface SearchProps {
    route?: string;
    searchResults?: {
        data?: Company[];
        total_records?: number;
        total_pages?: number;
    } | null;
}

const Search = ({ searchResults = null }: SearchProps) => {
    const { t } = useTranslation(["transporters"]);
    const { currentLocation, isPhoneVerified, companyId, serviceType, updateEventCount, eventCounts } =
        useContext(AppContext);
    const fetchOnClient = searchResults == null;
    const [shouldExecuteApi, setShouldExecuteApi] = useState(fetchOnClient);
    const [isBot, setIsBot] = useState(false);

    const isMobileScreen = useMediaQuery({ queryType: "mobile" });
    const router = useRouter();
    const [currentCityText, setCurrentCityText] = useState("");
    const { query } = router;
    const resultsHeaderRef = useRef<HTMLDivElement>(null);

    const { sendGAandMoEngageEvent } = useEventTrackerService();

    /***
     * States
     */
    const [isSrcSearchVisible, setisSrcSearchVisible] = useState(false);
    const [isDestSearchVisible, setisDestSearchVisible] = useState(false);
    const [isEditSearchModalVisible, setisEditSearchModalVisible] = useState(false);

    const [showOverlay, setshowOverlay] = useState<boolean>(false);

    const [totalParams, setTotalParams] = useState<number>();
    const [numOfParamsReady, updateNumOfParamsReady] = useState<number>();

    const [isLoadingData, setIsLoadingData] = useState(fetchOnClient);

    const [isSearchCardMinimized, setIsSearchCardMinimized] = useState(false);
    const [zoomInOut, setZoomInOut] = useState(false);

    const onStickyStateChanged = ({ status }: Sticky.Status) => {
        if (!zoomInOut) {
            setIsSearchCardMinimized(status === 1 || status === 2);
        }
    };

    let [data, setData] = useState<Array<Company>>(searchResults?.data || Array(12).fill(undefined));
    let [totalRecords, setTotalRecords] = useState<number | undefined>(searchResults?.total_records);

    let [currentPage, setcurrentPage] = useState<number>(1);
    let [pagesCount, setpagesCount] = useState<number>(5);

    const [searchBy, setSearchBy] = useState<DesktopProps["searchBy"]>("location");

    const [src, setSrc] = useState<CitySearchResultWithCateg | undefined>();
    const [dest, setDest] = useState<CitySearchResultWithCateg>();
    const [nameSearchVal, setNameSearchVal] = useState("");

    const [selectedDestQuickFilter, updateSelectedDestQuickFilter] = useState<DesktopProps["selectedDestQuickFilter"]>(
        {}
    );

    const { onSearchInput: onSrcSearchInput, searchOptions: srcSearchOptions } = usePlaceSearch({
        locationParamsOption: { source: LocationSearchSource.TransporterProfile }
    });
    const { onSearchInput: onDestSearchInput, searchOptions: destSearchOptions } = useStateSearch({
        locationParamsOption: { source: LocationSearchSource.TransporterProfile }
    });

    const isLoggedIn = Boolean(isPhoneVerified && companyId);

    const { mutate: transporterSearch } = useTransporterSearch();

    useEffect(() => {
        if (typeof navigator !== "undefined") {
            setIsBot(BOT_USER_AGENT_PATTERN.test(navigator.userAgent));
        }
    }, []);

    const checkRestrictedEvent = (name: RestrictedEvents) => {
        switch (name) {
            case "citySearchCount":
            case "transporterSearchCount": {
                const count = Number(eventCounts?.[name] || 0);
                return count >= 3;
            }
            case "pagination": {
                return true;
            }
            default: {
                return false;
            }
        }
    };

    const checkAndHandleGuestUserRestriction = (name: RestrictedEvents) => {
        if (!companyId) {
            if (checkRestrictedEvent(name)) {
                router?.push({ hash: "#Login" });
                return false;
            }
            updateEventCount?.(name);
        }
        return true;
    };

    const navigate = async () => {
        if (!checkAndHandleGuestUserRestriction?.("transporterSearchCount")) {
            return;
        }
        const searchBy = await getCurrentState(setSearchBy);
        const _src = await getCurrentState(setSrc);
        const srcCityNameFromDescription = _src?.city || removeTextAfterComma(_src?.name) || "";
        setCurrentCityText(srcCityNameFromDescription);
        const dest = await getCurrentState(setDest);
        const nameSearchVal = await getCurrentState(setNameSearchVal);
        const currPage = await getCurrentState(setcurrentPage);
        setTotalParams(undefined);
        updateNumOfParamsReady(undefined);
        if (searchBy === "location") {
            let arr: any = (query.params as string[]) || [];
            if (_src?.place_id && (dest?.state_id || dest?.id)) {
                setisSrcSearchVisible(false);
                setisDestSearchVisible(false);
                setisEditSearchModalVisible(false);
                let tempObj = { p: `${currPage}` };
                arr[0] = `${srcCityNameFromDescription}-to-${dest.state || dest.name}`
                    ?.replace?.(/ /g, "-")
                    ?.replace?.(/,/g, "-")
                    ?.replace?.(/--/g, "-")
                    ?.toLowerCase();
                router.query.params = arr;
                router.push(
                    {
                        pathname: router.pathname,
                        query: { ...router.query, ...tempObj }
                    },
                    undefined,
                    { shallow: true }
                );
            } else if (_src?.place_id) {
                setisSrcSearchVisible(false);
                setisDestSearchVisible(false);
                setisEditSearchModalVisible(false);
                let tempObj = { p: `${currPage}` };
                arr[0] = srcCityNameFromDescription
                    ?.replace?.(/ /g, "-")
                    ?.replace?.(/,/g, "-")
                    ?.replace?.(/--/g, "-")
                    ?.toLowerCase();
                router.query.params = arr;
                router.push(
                    {
                        pathname: router.pathname,
                        query: { ...router.query, ...tempObj }
                    },
                    undefined,
                    { shallow: true }
                );
            } else if (dest?.state_id || dest?.id) {
                setisSrcSearchVisible(false);
                setisDestSearchVisible(false);
                setisEditSearchModalVisible(false);
                let tempObj = { p: `${currPage}` };
                arr[0] = `for-${(dest.state || dest?.name)?.toLowerCase()}`
                    ?.replace?.(/ /g, "-")
                    ?.replace?.(/,/g, "-")
                    ?.replace?.(/--/g, "-");

                router.query.params = arr;
                router.push(
                    {
                        pathname: router.pathname,
                        query: { ...router.query, ...tempObj }
                    },
                    undefined,
                    { shallow: true }
                );
            } else {
                setisSrcSearchVisible(false);
                setisDestSearchVisible(false);
                setisEditSearchModalVisible(false);
                let tempObj = { p: `${currPage}` };
                router.query.params = [];
                router.push(
                    {
                        pathname: router.pathname,
                        query: { ...router.query, ...tempObj }
                    },
                    undefined,
                    { shallow: true }
                );
            }
        } else {
            if (nameSearchVal?.trim()) {
                setisSrcSearchVisible(false);
                setisDestSearchVisible(false);
                setisEditSearchModalVisible(false);
                let tempObj = { p: `${currPage}` };
                router.query.params = ["name", `${nameSearchVal || ""}`];
                router.push(
                    {
                        pathname: router.pathname,
                        query: { ...router.query, ...tempObj }
                    },
                    undefined,
                    { shallow: true }
                );
            } else {
                setisSrcSearchVisible(false);
                setisDestSearchVisible(false);
                setisEditSearchModalVisible(false);
                let tempObj = { p: `${currPage}` };
                router.query.params = ["name"];
                router.push(
                    {
                        pathname: router.pathname,
                        query: { ...router.query, ...tempObj }
                    },
                    undefined,
                    { shallow: true }
                );
            }
        }
        setShouldExecuteApi(true);
    };

    const getAndSetData = async (_searchBy?: "name" | "location", _nameSearchVal?: string) => {
        if (!shouldExecuteApi) return;
        setshowOverlay(false);
        const currentPage = await getCurrentState(setcurrentPage);
        if (currentPage <= pagesCount - 1) {
            setData(Array(12).fill(undefined));
            setIsLoadingData(true);
            const t1 = Date.now();
            transporterSearch(
                {
                    page: currentPage,
                    ...((_searchBy || searchBy) === "name" && (_nameSearchVal || nameSearchVal)?.trim()
                        ? { name: _nameSearchVal || nameSearchVal }
                        : {
                              sourcePlaceId: src?.place_id,
                              routeStateIds: dest?.id ? [dest?.id] : undefined
                          })
                },
                {
                    onSuccess: async (data) => {
                        if (data) {
                            data?.total_pages && setpagesCount(data?.total_pages > 4 ? 5 : data?.total_pages + 1);
                            data?.total_records && setTotalRecords(data?.total_records);
                            const t2 = Date.now();
                            if (t2 - t1 < 500) {
                                await sleep(500 - (t2 - t1));
                            }
                            if (data.data) {
                                ga4sendEvent("Directory_search_success_by_name_web", { value: 1 });
                                setData(
                                    data.data.map(
                                        ({
                                            routes: _routes,
                                            connections: _connections,
                                            ...result
                                        }: Company & {
                                            routes?: unknown;
                                            connections?: unknown;
                                        }) => result as Company
                                    )
                                );
                            } else {
                                ga4sendEvent("Directory_search_success_by_name_web", { value: 0 });
                                setshowOverlay(true);
                            }
                            setIsLoadingData(false);
                        }
                        setShouldExecuteApi(false);
                    },
                    onError: () => {}
                }
            );
        } else {
            setshowOverlay(true);
        }
    };

    const handleClick = async (page: number) => {
        if (checkAndHandleGuestUserRestriction?.("pagination")) {
            setcurrentPage(page);
            if (page < pagesCount) {
                navigate();
            } else {
                setshowOverlay(true);
                typeof window !== "undefined" && window.scrollTo({ left: 0, top: 0, behavior: "smooth" });
            }
        }
    };

    const onChangeSearchBy = (e: any) => {
        if (e.target.value.includes("name")) ga4sendEvent("Directory_search_transporter_type_web", { value: 0 });
        else ga4sendEvent("Directory_search_transporter_type_web", { value: 1 });
        if (e.target.checked) {
            setSearchBy(e.target.value as typeof searchBy);
            setcurrentPage(1);
            setpagesCount(5);
            navigate();
        }
    };

    const onSelectSrc = async (selectedCity?: CitySearchResultWithCateg) => {
        setSrc(selectedCity);
        setcurrentPage(1);
        setpagesCount(5);
        navigate();

        sendGAandMoEngageEvent({
            name: GA4EventNames.TRANSPORT_DIRECTORY.transport_directory_source_city_entered,
            data: {
                source_screen: "transport_directory"
            }
        });
    };

    const onClearSrc = () => {
        setSrc(undefined);
        setcurrentPage(1);
        setpagesCount(5);
        navigate();
    };

    const onSelectDest = async (selectedState: CitySearchResultWithCateg | undefined) => {
        setDest(selectedState);
        setcurrentPage(1);
        setpagesCount(5);
        navigate();

        sendGAandMoEngageEvent({
            name: GA4EventNames.TRANSPORT_DIRECTORY.transport_directory_destination_city_entered,
            data: {
                source_screen: "transport_directory"
            }
        });
    };

    const onInputNameSearch = (e: any) => {
        setNameSearchVal(e.target.value);
        setpagesCount(5);
        setcurrentPage(1);

        sendGAandMoEngageEvent({
            name: GA4EventNames.TRANSPORT_DIRECTORY.transport_directory_search_by_name_entered,
            data: {
                source_screen: "transport_directory"
            }
        });
    };

    const nameSearchDebounceVal = useDebounce(nameSearchVal, 600);

    useEffect(() => {
        if (searchBy == "name" && nameSearchDebounceVal !== null && nameSearchDebounceVal !== undefined) {
            navigate();
        }
    }, [nameSearchDebounceVal, searchBy]);

    const onClearName = async () => {
        setNameSearchVal("");
        setcurrentPage(1);
        setpagesCount(5);
        navigate();
    };

    const onClickUpdateSearch: MobileProps["onClickUpdateSearch"] = ({ searchBy, src, dest, name }) => {
        setSearchBy(searchBy);
        if (searchBy === "location") {
            setSrc(src);
            setDest(dest);
        } else {
            setNameSearchVal(name);
        }
        setcurrentPage(1);
        setpagesCount(5);
        navigate();
    };

    useEffect(() => {
        // if (scrollPos !== 0) {
        //     if (location?.state?.isEditSearchModalVisible) {
        //     } else if (location?.state?.isSrcSearchVisible || location?.state?.isDestSearchVisible) {
        //         window.scrollTo(0, 0);
        //     } else {
        //         window.scrollTo({ left: 0, top: +getHeightByViewPort(74 - 55).slice(0, -2), behavior: "smooth" });
        //     }
        // }
        // const { p } = qs.parse(location.search);
        setcurrentPage(+(query.p || 1));
        const paths = (router.query.params as string[]) ? (router.query.params as string[])[0] : undefined;
        if (paths?.includes("name")) {
            setSearchBy("name");
            setNameSearchVal((router.query.params as string[])[1]);
            getAndSetData("name", (router.query.params as string[])[1]);
        } else {
            setSearchBy("location");
            let src, dest;
            const locationParam = (router.query.params as string[]) ? (router.query.params as string[])[0] : undefined;
            if (locationParam?.includes("-to-")) {
                [src, dest] = locationParam.split("-to-").map((p) => p.replace(/-/g, " "));
            } else if (locationParam?.includes("for-") && router.asPath.includes("/for")) {
                dest = locationParam.split("for-")[1].replace(/-/g, " ");
            } else {
                src = locationParam?.replace(/-/g, " ");
            }
            setpagesCount(5);
            setTotalParams((!!src ? 1 : 0) + (!!dest ? 1 : 0));
            updateNumOfParamsReady(0);
            if (shouldExecuteApi) {
                src ? onSrcSearchInput(src as string) : setSrc(undefined);
                dest ? onDestSearchInput(dest as string) : setDest(undefined);
            }
        }
    }, [router.query]);

    useEffect(() => {
        if (srcSearchOptions?.length > 0) {
            const resolvedSrc = srcSearchOptions[0];
            setSrc(resolvedSrc);
            setCurrentCityText(resolvedSrc?.city || removeTextAfterComma(resolvedSrc?.name) || "");
            updateNumOfParamsReady((state) => (state || 0) + 1);
        }
    }, [srcSearchOptions]);

    useEffect(() => {
        if (destSearchOptions?.[0]) {
            setDest(destSearchOptions[0]);
            updateSelectedDestQuickFilter(destSearchOptions[0]);
            updateNumOfParamsReady((state) => (state || 0) + 1);
        }
    }, [destSearchOptions]);

    useEffect(() => {
        if (numOfParamsReady !== undefined && totalParams !== undefined && numOfParamsReady === totalParams) {
            getAndSetData();
        }
    }, [numOfParamsReady]);

    const pageLoadParam = getPageLoadSourceUTMParam({
        urlQueryParams: router.query,
        utmParamObj: transporterUtmParam
    });

    (() => {
        if (typeof window !== "undefined") {
            window.addEventListener("resize", () => {
                if (window.devicePixelRatio > 2 || window.devicePixelRatio < 2) {
                    setZoomInOut(true);
                } else {
                    setZoomInOut(false);
                }
            });
        }
    })();

    const city =
        searchBy === "location" && query.params !== undefined ? query.params?.[0] && capitalize(query.params[0]) : "";
    const IN_CITY_TEXT = `${city ? "in " + city : ""}`;
    const META_TITLE = `Top Transporters ${
        IN_CITY_TEXT ? IN_CITY_TEXT : "Directory in India"
    } | Book Online Transport Service`;
    const META_DESCRIPTION = city
        ? `Discover trustworthy transporters ${IN_CITY_TEXT} for efficient and secure logistics solutions. Vahak provides a platform to connect with reliable transportation services tailored to your needs.`
        : "Join India's largest verified transporter directory. Connect and book transporters online on Vahak for transportation services anytime, anywhere. Enjoy a seamless and trusted transport community.";

    const [topPadding, setTopPadding] = useState<string>(isMobileScreen ? "100px" : "105px");
    const [stickyHeaderTop, setStickyHeaderTop] = useState(0);

    useEffect(() => {
        if (isPhoneVerified && companyId) {
            setTopPadding("0px");
            setStickyHeaderTop(0);
        } else if (isPhoneVerified && !companyId) {
            setTopPadding(isMobileScreen ? "100px" : "105px");
            setStickyHeaderTop(isMobileScreen ? 100 : 105);
        } else {
            setTopPadding(isMobileScreen ? "55px" : "65px");
            setStickyHeaderTop(isMobileScreen ? 0 : 65);
        }
    }, [isPhoneVerified, companyId, isMobileScreen]);

    useEffect(() => {
        if (router.isReady) {
            let params = router.query.params;
            if (Array.isArray(params)) {
                params = params?.map((param) =>
                    param?.replace?.(/ /g, "-")?.replace?.(/,/g, "-")?.replace?.(/--/g, "-")
                );
            }
            router.push(
                {
                    query: { params: params }
                },
                undefined,
                { shallow: false }
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.isReady]);

    const sourceCityToUseForSeoContent = currentCityText;

    const shouldIndex = (totalRecords ?? searchResults?.total_records ?? 0) >= 5;

    return (
        <>
            <PageMetaTags
                title={META_TITLE}
                description={META_DESCRIPTION}
                shouldBeIndexed={shouldIndex}
                canonicalUrl={`https://www.vahak.in${ROUTES.transporters.getUrl(sourceCityToUseForSeoContent)}`}
                ogUrl={`https://www.vahak.in${ROUTES.transporters.getUrl(sourceCityToUseForSeoContent)}`}
                ogImgSrc="https://d1rgemtytdlz2g.cloudfront.net/web_og_image_jan_22.png"
                ogImgAltText={`Connect with India's verified transport services through Vahaks online Transport Directory. Search for logistics company, move your goods easily and get them delivered anywhere in India. Connect now with transport services in ${
                    searchBy === "location" && query.params !== undefined ? query.params && query.params[0] : "India"
                }`}
                schemaOrgJsonUrl={ROUTES.transporters.getUrl(sourceCityToUseForSeoContent)}
                breadcrumbList={
                    searchBy === "location" && query.params !== undefined
                        ? breadcrumbsConstants({ city: query.params[0] }).transportDirectoryLocation
                        : breadcrumbsConstants({}).transportDirectory
                }
                faqList={sourceCityToUseForSeoContent ? getFaqListByCity(sourceCityToUseForSeoContent).faqList : []}
            />

            <section
                style={{
                    paddingBottom: 0,
                    position: "relative",
                    paddingTop: topPadding
                }}
                className={classnames(isMobileScreen ? "mobilePage" : "desktopPage")}
            >
                {currentLocation?.status !== "loading" ? (
                    <>
                        {isMobileScreen ? (
                            <Sticky
                                onStateChange={onStickyStateChanged}
                                enabled
                                bottomBoundary="#resultsContainer"
                                top={55}
                                innerZ={99}
                                className={searchStyles.stickyOuterWrapper}
                            >
                                <Mobile
                                    isSearchCardMinimized={isSearchCardMinimized}
                                    topContainerClassName={classnames(
                                        searchStyles.topContainer,
                                        "topContainer",
                                        searchBy === "name" && searchStyles.searchByNameTopContainer,
                                        isSearchCardMinimized && "sticky",
                                        isSearchCardMinimized && searchStyles.sticky
                                    )}
                                    searchCardClassName={classnames(
                                        "searchCardContainer",
                                        searchStyles.searchCardContainer
                                    )}
                                    searchBy={searchBy}
                                    onChangeSearchBy={onChangeSearchBy}
                                    src={src}
                                    onSelectSrc={onSelectSrc}
                                    onClearSrc={onClearSrc}
                                    dest={dest}
                                    onSelectDest={onSelectDest}
                                    selectedDestQuickFilter={selectedDestQuickFilter}
                                    nameSearchVal={nameSearchVal}
                                    onInputNameSearch={onInputNameSearch}
                                    onClearName={onClearName}
                                    onClickUpdateSearch={onClickUpdateSearch}
                                    isSrcSearchVisible={isSrcSearchVisible}
                                    setisSrcSearchVisible={setisSrcSearchVisible}
                                    isDestSearchVisible={isDestSearchVisible}
                                    setisDestSearchVisible={setisDestSearchVisible}
                                    isEditSearchModalVisible={isEditSearchModalVisible}
                                    setisEditSearchModalVisible={setisEditSearchModalVisible}
                                    srcParam={capitalize(sourceCityToUseForSeoContent ?? "")}
                                />
                            </Sticky>
                        ) : (
                            <Sticky
                                onStateChange={onStickyStateChanged}
                                enabled
                                bottomBoundary="#resultsContainer"
                                // top={"#resultsContainer"}
                                // top={0}
                                top={stickyHeaderTop}
                                innerZ={!isSearchCardMinimized ? 1 : 99}
                                className={classnames(
                                    searchStyles.transporter_sticky_wrapper,
                                    isSearchCardMinimized && searchStyles.active
                                )}
                            >
                                {!isSearchCardMinimized ? (
                                    <Desktop
                                        isSearchCardMinimized={false}
                                        topContainerClassName={classnames("topContainer")}
                                        coverClassName={classnames("cover")}
                                        searchCardClassName={"searchCardContainer"}
                                        searchBy={searchBy}
                                        onChangeSearchBy={onChangeSearchBy}
                                        src={src}
                                        onSelectSrc={onSelectSrc}
                                        dest={dest}
                                        onSelectDest={onSelectDest}
                                        selectedDestQuickFilter={selectedDestQuickFilter}
                                        nameSearchVal={nameSearchVal}
                                        onInputNameSearch={onInputNameSearch}
                                        onClearName={onClearName}
                                        srcParam={capitalize(sourceCityToUseForSeoContent ?? "")}
                                    />
                                ) : (
                                    <DesktopSearchCard
                                        isSearchCardMinimized={true}
                                        searchBy={searchBy}
                                        onChangeSearchBy={onChangeSearchBy}
                                        src={src}
                                        onSelectSrc={onSelectSrc}
                                        dest={dest}
                                        onSelectDest={onSelectDest}
                                        selectedDestQuickFilter={selectedDestQuickFilter}
                                        nameSearchVal={nameSearchVal}
                                        onInputNameSearch={onInputNameSearch}
                                        onClearName={onClearName}
                                    />
                                )}
                            </Sticky>
                        )}
                        <div className="container">
                            <>
                                <div
                                    className={classnames(
                                        isMobileScreen
                                            ? "transporterSearch__results__mobile"
                                            : "transporterSearch__results__desktop",
                                        isSearchCardMinimized ? searchStyles.transporterSearchResult : ""
                                    )}
                                >
                                    <div
                                        className="mainDivAlign"
                                        style={{
                                            marginTop: isMobileScreen ? "33px" : "40px",
                                            marginBottom: isMobileScreen ? "18px" : "12px"
                                        }}
                                    >
                                        <h2
                                            className={searchStyles.transporters_heading}
                                            style={{
                                                fontSize: isMobileScreen ? getRemValue(16) : getRemValue(20),

                                                fontFamily: "Inter-Bold",
                                                lineHeight: isMobileScreen ? "20px" : "28px",
                                                letterSpacing: isMobileScreen ? getRemValue(-0.16) : getRemValue(-0.22)
                                            }}
                                            ref={resultsHeaderRef}
                                        >
                                            {!isLoadingData ? (
                                                isMobileScreen ? (
                                                    t("transporter_profile_section.title_mobile")
                                                ) : src && searchBy === "location" ? (
                                                    `Showing ${
                                                        totalRecords || ""
                                                    } Transporters around ${sourceCityToUseForSeoContent}`
                                                ) : (
                                                    t("transporter_profile_section.title_desktop")
                                                )
                                            ) : (
                                                <Skeleton width={isMobileScreen ? "150px" : "350px"} />
                                            )}
                                        </h2>
                                        <div
                                            style={{
                                                color: "#202124",
                                                fontSize: isMobileScreen ? getRemValue(11) : getRemValue(16),
                                                letterSpacing: isMobileScreen ? getRemValue(1.98) : getRemValue(3.96),
                                                marginLeft: "auto",
                                                marginRight: isMobileScreen ? "10px" : "12px",
                                                lineHeight: isMobileScreen ? "12px" : "23px",

                                                fontFamily: "var(--font-medium)"
                                            }}
                                        >
                                            {!isLoadingData ? (
                                                `${currentPage}/${pagesCount}`
                                            ) : (
                                                <Skeleton width="50px" />
                                            )}
                                        </div>

                                        <AppPagination
                                            currentPage={currentPage}
                                            pagesCount={pagesCount}
                                            handleClick={handleClick}
                                            isMobile={isMobileScreen}
                                        />
                                    </div>
                                    <div className={searchStyles.searchedCityDesc}>
                                        <ShowMoreText
                                            limit={
                                                isBot
                                                    ? getFaqListByCity(sourceCityToUseForSeoContent ?? "India")
                                                          .titleH1Desc.length
                                                    : 100
                                            }
                                            truncatedEnding=""
                                            more={<Typography weight="semibold">Read More +</Typography>}
                                            text={getFaqListByCity(sourceCityToUseForSeoContent ?? "India").titleH1Desc}
                                        />
                                    </div>
                                    {/* {showOverlay && <Overlay />} */}

                                    <SearchResultsContainer
                                        data={data}
                                        className={classnames(
                                            "cardContainer",
                                            showOverlay && "TransporterSearch__Overlay"
                                        )}
                                        id="resultsContainer"
                                    />

                                    <div className="alignPaginationDiv" id="bottomPagination">
                                        <BottomPagination
                                            currentPage={currentPage}
                                            pagesCount={pagesCount}
                                            handleClick={handleClick}
                                            isMobile={isMobileScreen}
                                        />
                                    </div>
                                </div>
                            </>
                        </div>
                    </>
                ) : (
                    <div className="d-flex justify-content-center" style={{ height: "80vh", alignItems: "center" }}>
                        <div className="spinner-border" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                    </div>
                )}
            </section>
            <section className={searchStyles.ctaSection}>
                <div className={searchStyles.contentContainer}>
                    <div className={searchStyles.content}>
                        <div>
                            <Typography as="h3" size="xxl" mSize="m" color={COLORS.WHITE}>
                                Get Premium Features & Priority Support!
                            </Typography>
                        </div>
                        <Button
                            size="m"
                            onClick={() => {
                                router.push(ROUTES.membershipUpgrade.getUrl());
                            }}
                            color="tertiary"
                        >
                            Upgrade Now!
                        </Button>
                    </div>
                    <AppImg
                        src="online-truck-booking_desktop.png"
                        alt="cta image"
                        layout="fixed"
                        width={isMobileScreen ? 236 : 440}
                        height={isMobileScreen ? 135 : 255}
                    />
                </div>
            </section>
            {!!sourceCityToUseForSeoContent && (
                <section className={classnames("container", searchStyles.cityExtendedDesc)}>
                    <CityDescription city={sourceCityToUseForSeoContent} />
                </section>
            )}

            <CTABanner
                href={PLAYSTORE_URLS.transporterSearchPage}
                title={t("header_filter_section.bottom_cta.heading")}
            />

            {/* <EventScriptOnLoad eventName={"Directory_For_type_of_user_web"} params={{ value: isLoggedIn ? 1 : 0 }} />
            {pageLoadParam && <EventScriptOnLoad eventName={"Directory_coming_location_web"} params={pageLoadParam} />} */}

            {!isLoggedIn ? (
                <MandateServiceType
                    isLoggedIn={isLoggedIn}
                    initialServiceType={serviceType}
                    source={"transport_directory"}
                />
            ) : (
                <></>
            )}
        </>
    );
};

export default Search;

const removeTextAfterComma = (text: string | undefined) => {
    return text?.replace(/,.*/, "");
};
